import React, { useEffect, useMemo, useState } from "react";
import "../styles/LeaveTypesPage.css";
import { useI18n } from "../providers/I18nProvider";
import PaginationControls from "../components/PaginationControls";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRoleName, ROLE } from "../utils/roles";
import { useConfirm } from "../providers/ConfirmProvider";
import NoirAlert from "../components/NoirAlert";
import { TabBar, FilterBar, SearchInput, EmptyState, countBy } from "../components/ListToolbar";

const LeaveTypesPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [types, setTypes] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const notifyError = (err) =>
    setNotification({
      message: err.response?.data?.msg || err.message || t("common.genericError"),
      type: "error",
    });

  const [tab, setTab] = useState("all"); // all | daily | hourly | affects | noAffect
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name_asc");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // leave type object or null
  const [form, setForm] = useState({
    name: "",
    description: "",
    affects_balance: true,
    is_duration_flexible: false,
    default_duration_days: "",
    default_duration_hours: "",
    default_quota_days: "",
    default_quota_hours: "",
  });

  const canManage = useMemo(() => {
    const role = normalizeRoleName(me?.role_name);
    return role === ROLE.ADMIN || role === ROLE.COMPANY_MANAGER || role === ROLE.IT;
  }, [me]);

  const pageSizeKey = "pageSize.leaveTypes";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const tabCounts = useMemo(
    () =>
      countBy(types, {
        all: () => true,
        daily: (x) => !x.is_duration_flexible,
        hourly: (x) => !!x.is_duration_flexible,
        affects: (x) => !!x.affects_balance,
        noAffect: (x) => !x.affects_balance,
      }),
    [types]
  );

  const filteredTypes = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const list = types.filter((x) => {
      if (tab === "daily" && x.is_duration_flexible) return false;
      if (tab === "hourly" && !x.is_duration_flexible) return false;
      if (tab === "affects" && !x.affects_balance) return false;
      if (tab === "noAffect" && x.affects_balance) return false;
      if (q && !`${x.name || ""} ${x.description || ""}`.toLocaleLowerCase("tr-TR").includes(q)) {
        return false;
      }
      return true;
    });
    return [...list].sort((a, b) =>
      sortKey === "name_desc"
        ? String(b.name || "").localeCompare(String(a.name || ""), "tr-TR")
        : String(a.name || "").localeCompare(String(b.name || ""), "tr-TR")
    );
  }, [types, tab, search, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, sortKey]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredTypes.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredTypes.length, page, pageSize]);

  const pagedTypes = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTypes.slice(start, start + pageSize);
  }, [filteredTypes, page, pageSize]);

  useEffect(() => {
    const fetchTypes = async () => {
      setLoadError("");
      try {
        const res = await apiClient.get("/leave-types");
        setTypes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setLoadError(err.response?.data?.msg || err.message || t("leaveTypes.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
    // dil değişiminde yeniden yüklemeye gerek yok
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      description: "",
      affects_balance: true,
      is_duration_flexible: false,
      default_duration_days: "",
      default_duration_hours: "",
      default_quota_days: "",
      default_quota_hours: "",
    });
    setShowModal(true);
  };

  const openEdit = (type) => {
    setEditing(type);
    setForm({
      name: type.name || "",
      description: type.description || "",
      affects_balance: !!type.affects_balance,
      is_duration_flexible: !!type.is_duration_flexible,
      default_duration_days: type.default_duration_days ?? "",
      default_duration_hours: type.default_duration_hours ?? "",
      default_quota_days: type.default_quota_days ?? "",
      default_quota_hours: type.default_quota_hours ?? "",
    });
    setShowModal(true);
  };

  const refresh = async () => {
    const res = await apiClient.get("/leave-types");
    setTypes(Array.isArray(res.data) ? res.data : []);
  };

  const save = async () => {
    if (saving) return;
    if (!form.name.trim()) {
      setNotification({ message: t("leaveTypesPage.nameRequired"), type: "error" });
      return;
    }
    setSaving(true);
    try {

      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        affects_balance: !!form.affects_balance,
        is_duration_flexible: !!form.is_duration_flexible,
        default_duration_days: form.default_duration_days === "" ? null : Number(form.default_duration_days),
        default_duration_hours: form.default_duration_hours === "" ? null : Number(form.default_duration_hours),
        default_quota_days: form.default_quota_days === "" ? null : Number(form.default_quota_days),
        default_quota_hours: form.default_quota_hours === "" ? null : Number(form.default_quota_hours),
      };

      if (editing?.id) {
        await apiClient.put(`/leave-types/${editing.id}`, payload);
      } else {
        await apiClient.post("/leave-types", payload);
      }
      setShowModal(false);
      await refresh();
      setNotification({ message: t("common.saved"), type: "success" });
    } catch (err) {
      // Modal açık kalsın, kullanıcı girdiğini kaybetmesin
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type) => {
    const ok = await confirm(t("leaveTypes.confirmDelete") || "Silinsin mi?");
    if (!ok) return;
    try {
      await apiClient.delete(`/leave-types/${type.id}`);
      await refresh();
      setNotification({ message: t("common.saved"), type: "success" });
    } catch (err) {
      notifyError(err);
    }
  };

  if (loading) return <div className="leave-types-page">{t("common.loading")}</div>;
  if (loadError) return <div className="leave-types-page error">{loadError}</div>;

  return (
    <div className="leave-types-page">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="leave-types-header">
        <h2>{t("leaveTypes.title")}</h2>
        {canManage && (
          <button className="primary" onClick={openCreate}>
            {t("leaveTypes.newType") || "Yeni izin türü"}
          </button>
        )}
      </div>
      {types.length === 0 ? (
        <EmptyState message={t("leaveTypes.empty")} />
      ) : (
        <div className="table-wrap">
          <TabBar
            value={tab}
            onChange={setTab}
            ariaLabel={t("leaveTypes.title")}
            tabs={[
              { key: "all", label: t("leaveTypesPage.tabs.all"), count: tabCounts.all },
              { key: "daily", label: t("leaveTypesPage.tabs.daily"), count: tabCounts.daily },
              { key: "hourly", label: t("leaveTypesPage.tabs.hourly"), count: tabCounts.hourly },
              { key: "affects", label: t("leaveTypesPage.tabs.affects"), count: tabCounts.affects },
              { key: "noAffect", label: t("leaveTypesPage.tabs.noAffect"), count: tabCounts.noAffect },
            ]}
          />
          <FilterBar showReset={Boolean(search)} onReset={() => setSearch("")}>
            <SearchInput value={search} onChange={setSearch} placeholder={t("leaveTypesPage.search")} />
            <select
              className="leaves-filter-input lt-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("admin.leaves.filters.sort")}
            >
              <option value="name_asc">{t("leaveTypesPage.sortNameAsc")}</option>
              <option value="name_desc">{t("leaveTypesPage.sortNameDesc")}</option>
            </select>
          </FilterBar>
          {filteredTypes.length === 0 && <EmptyState message={t("admin.leaves.emptyFiltered")} />}
          <PaginationControls
            totalItems={filteredTypes.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <table>
            <thead>
              <tr>
                <th>{t("leaveTypes.columns.name")}</th>
                <th>{t("leaveTypes.columns.description")}</th>
                <th>{t("leaveTypes.columns.affectsBalance")}</th>
                <th>{t("leaveTypes.columns.type")}</th>
                <th>{t("leaveTypes.columns.defaultDuration")}</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {pagedTypes.map((type) => (
                <tr key={type.id}>
                  <td>{type.name}</td>
                  <td>{type.description || "-"}</td>
                  <td>{type.affects_balance ? t("common.yes") : t("common.no")}</td>
                  <td>
                    {type.is_duration_flexible
                      ? t("leaveTypes.duration.hourly")
                      : t("leaveTypes.duration.daily")}
                  </td>
                  <td>
                    {type.is_duration_flexible
                      ? type.default_duration_hours ?? "-"
                      : type.default_duration_days ?? "-"}
                  </td>
                  {canManage && (
                    <td className="actions-cell" style={{ whiteSpace: "nowrap" }}>
                      <button className="ghost" onClick={() => openEdit(type)}>
                        {t("common.edit")}
                      </button>{" "}
                      <button className="danger" onClick={() => remove(type)}>
                        {t("common.delete")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            totalItems={filteredTypes.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop" onMouseDown={() => setShowModal(false)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{editing ? (t("leaveTypes.editType") || "İzin türünü düzenle") : (t("leaveTypes.newType") || "Yeni izin türü")}</h3>
            <div className="form-grid">
              <label>
                {t("leaveTypes.columns.name")}
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </label>
              <label>
                {t("leaveTypes.columns.description")}
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
              <label>
                {t("leaveTypes.columns.affectsBalance")}
                <select
                  value={String(!!form.affects_balance)}
                  onChange={(e) => setForm({ ...form, affects_balance: e.target.value === "true" })}
                >
                  <option value="true">{t("common.yes")}</option>
                  <option value="false">{t("common.no")}</option>
                </select>
              </label>
              <label>
                {t("leaveTypes.columns.type")}
                <select
                  value={String(!!form.is_duration_flexible)}
                  onChange={(e) => setForm({ ...form, is_duration_flexible: e.target.value === "true" })}
                >
                  <option value="false">{t("leaveTypes.duration.daily")}</option>
                  <option value="true">{t("leaveTypes.duration.hourly")}</option>
                </select>
              </label>

              {!form.is_duration_flexible ? (
                <label>
                  {t("leaveTypes.columns.defaultDuration")}
                  <input
                    type="number"
                    step="0.5"
                    value={form.default_duration_days}
                    onChange={(e) => setForm({ ...form, default_duration_days: e.target.value })}
                  />
                </label>
              ) : (
                <label>
                  {t("leaveTypes.columns.defaultDuration")}
                  <input
                    type="number"
                    step="0.5"
                    value={form.default_duration_hours}
                    onChange={(e) => setForm({ ...form, default_duration_hours: e.target.value })}
                  />
                </label>
              )}

              {!form.is_duration_flexible ? (
                <label>
                  {t("leaveTypes.defaultQuotaDays") || "Varsayılan kota (gün)"}
                  <input
                    type="number"
                    step="0.5"
                    value={form.default_quota_days}
                    onChange={(e) => setForm({ ...form, default_quota_days: e.target.value })}
                  />
                </label>
              ) : (
                <label>
                  {t("leaveTypes.defaultQuotaHours") || "Varsayılan kota (saat)"}
                  <input
                    type="number"
                    step="0.5"
                    value={form.default_quota_hours}
                    onChange={(e) => setForm({ ...form, default_quota_hours: e.target.value })}
                  />
                </label>
              )}
            </div>

            <div className="actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button className="ghost" onClick={() => setShowModal(false)}>
                {t("common.cancel")}
              </button>
              <button className="primary" onClick={save} disabled={saving}>
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypesPage;
